import { ComponentRef } from '@angular/core';
import { ActivatedRouteSnapshot, BaseRouteReuseStrategy, DetachedRouteHandle, Route } from '@angular/router';

/** Marks a route whose component tree should survive navigating away and back. */
export const REUSE_ROUTE_DATA_KEY = 'reuseComponent';

/**
 * How many reusable routes of each kind stay in memory at once. Each one holds a whole
 * component tree and its detached DOM — a category list with fifty pages back-filled is a
 * lot of nodes — so this is a memory cap, not a correctness one.
 *
 * The budget is per kind rather than shared across all of them. Browsing a category means a
 * run of products, and under one shared budget the third product would push out the category
 * the run started from — evicting the most valuable entry in the cache to keep the least.
 */
const MAX_STORED_ROUTES_PER_KIND = 3;

/**
 * Hooks a cached component may implement. A detached component is not destroyed, so
 * anything it holds in shared, application-level state has to be parked on the way out
 * and put back on the way in.
 */
export interface ReusableRouteComponent {
  onRouteDetach?(): void;

  /**
   * `snapshot` is the navigation being attached to, not the one the component was built for.
   * The router advances `ActivatedRoute.snapshot` only after this returns, so a component
   * that cares about the incoming url — a fragment, a query param — has to read it here
   * rather than from its own injected route.
   *
   * The view is not back in the document at this point either, and the navigation is still
   * activating: anything that measures, scrolls, or asks the router to navigate has to wait.
   */
  onRouteAttach?(snapshot: ActivatedRouteSnapshot): void;
}

/**
 * The shape the router stores for a detached subtree. Not part of the public
 * DetachedRouteHandle type, which is opaque, but it is what
 * `detachAndStoreRouteSubtree` passes in — checked against the installed
 * @angular/router (_router-chunk.mjs).
 */
interface StoredRouteHandle {
  componentRef: ComponentRef<ReusableRouteComponent>;
}

/** A cached subtree, with the route config its memory budget is counted against. */
interface StoredRoute {
  handle: DetachedRouteHandle;
  kind: Route | null;
}

/**
 * Keeps a page alive across a round trip to another one.
 *
 * The component instance and its DOM survive, so height and scroll are preserved with no
 * refetch and no flicker. For a list that also means the accumulated items array is still in
 * memory — which fixes the Load more case for free within a session. The ?page=a-b range
 * handles every case where there is no cached component to return to: refresh, shared link,
 * new tab, cold SSR. The two are complementary, not alternatives.
 *
 * Nothing refetches on the way back, so prices, stock and review counts are as old as the
 * visit that cached them. That is the accepted cost, and it is a display one: the cart prices
 * every item server side from its id, so a stale price on screen cannot become a stale order.
 *
 * What a cached page does have to do itself is republish everything it writes outside its own
 * template — meta tags, analytics, json-ld — since none of that is restored by re-inserting
 * the DOM. `ReusableRouteComponent` is where that happens.
 */
export class DynamicSlugRouteReuseStrategy extends BaseRouteReuseStrategy {
  private readonly stored = new Map<string, StoredRoute>();

  override shouldReuseRoute(
    future: ActivatedRouteSnapshot,
    curr: ActivatedRouteSnapshot,
  ): boolean {
    if (future.routeConfig !== curr.routeConfig) return false;
    if (future.routeConfig?.path === ':slug') {
      return future.params['slug'] === curr.params['slug'];
    }
    return true;
  }

  override shouldDetach(route: ActivatedRouteSnapshot): boolean {
    return route.routeConfig?.data?.[REUSE_ROUTE_DATA_KEY] === true;
  }

  override store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
    const key = this.keyOf(route);

    // The router clears the entry immediately after retrieving it, on the attach path —
    // which makes this the one place that fires exactly once per attach.
    if (!handle) {
      const attached = this.stored.get(key);
      this.stored.delete(key);
      this.componentOf(attached?.handle)?.onRouteAttach?.(route);
      return;
    }

    this.stored.delete(key);
    this.stored.set(key, { handle, kind: route.routeConfig });
    this.componentOf(handle)?.onRouteDetach?.();
    this.evictOldestOfKind(route.routeConfig);
  }

  override shouldAttach(route: ActivatedRouteSnapshot): boolean {
    return this.stored.has(this.keyOf(route));
  }

  override retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
    return this.stored.get(this.keyOf(route))?.handle ?? null;
  }

  /** Trims one kind of route back to its budget, oldest first — the map keeps insertion order. */
  private evictOldestOfKind(kind: Route | null): void {
    const keys = [...this.stored]
      .filter(([, storedRoute]) => storedRoute.kind === kind)
      .map(([key]) => key);

    // Clamped: `slice(0, -1)` counts from the end and would evict everything but the newest
    // the moment there were fewer entries than the budget allows.
    const overBudget = Math.max(0, keys.length - MAX_STORED_ROUTES_PER_KIND);

    for (const key of keys.slice(0, overBudget)) {
      const evicted = this.stored.get(key);
      this.stored.delete(key);
      (evicted?.handle as unknown as StoredRouteHandle)?.componentRef?.destroy();
    }
  }

  /**
   * The path, without query params. Two categories are different entries; the same
   * category under a different language prefix is too, since the routes are one shared
   * config array reused under both. Query params are excluded on purpose — the range and
   * the filters live in the cached component, and the router restores the URL itself.
   */
  private keyOf(route: ActivatedRouteSnapshot): string {
    return route.pathFromRoot
      .map(snapshot => snapshot.url.map(segment => segment.path).join('/'))
      .filter(Boolean)
      .join('/');
  }

  private componentOf(handle: DetachedRouteHandle | undefined): ReusableRouteComponent | null {
    return (handle as unknown as StoredRouteHandle)?.componentRef?.instance ?? null;
  }
}
